import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, CheckCircle2, CircleDollarSign, FileText, Hash, ListOrdered, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { unwrap } from "../../lib/api-envelope";
import { fmtCurrency } from "../../lib/display-labels";
import { formatInstant } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel, DhSpec } from "../downhole/DownholeBits";
import { FieldTicket, StatusBadge, TICKET_TYPE, periodLabel, signedFlag, toTicket } from "./rental-field-ticket";

/**
 * One rental field ticket, /rentals/tickets/:id: full page, the layout of
 * casing's and downhole's ticket pages. Band (Total / Days / Lines / PO, the
 * actions), the ticket record, the line items with the ticket's STORED
 * subtotal, tax and total, and the two sign-off cards.
 *
 * Was the right half of a split pane on /rentals/tickets until #815.
 *
 * Every figure is what GET /surface-rentals/tickets/:id sent. Nothing is
 * summed here: "Lines" counts the rows, "Days" is the ticket's own
 * `total_days` (null on a several-line ticket, so —), and the tax is
 * `tax_amount` named by `tax_type` (api#1019), not `gst_amount`.
 */

// Filled with the band's own navy, as on the trucking invoice band (fe#962):
// unfilled, the hero circle's ring shows through behind the label and takes it
// below Quotes' contrast at wide viewports (fe#963).
const BAND_SECONDARY =
  "inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 bg-fi-brand px-4 text-sm font-bold transition hover:bg-white/10 disabled:opacity-50";
const PRIMARY = "fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50";
const TH = "px-4 py-3 text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted";
const TD = "px-4 py-3 text-sm text-fieldiq-text";
const TD_NUM = "whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text";

// NOT WIRED, AND NOT WIRABLE FROM HERE. The API invoices an ORDER
// (POST /surface-rentals/invoices/generate-from-order/:id); there has never been
// a ticket-to-invoice route. The split pane opened a confirm dialog naming the
// ticket's raw id and then said this; it now says it straight away.
function handleApproveInvoice() {
  toast.error("Generating an invoice from a field ticket is not supported yet.");
}
// No dispute route on the rentals router and no column to hold one.
function handleDispute() {
  toast.error("Disputing a field ticket is not supported yet.");
}
// No signature-request route on the rentals router.
function handleSendForSignature() {
  toast.error("Sending a field ticket for signature is not supported yet.");
}

export default function RentalFieldTicketDetail() {
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const timeZone = useTenantTimeZone();

  const query = useQuery({
    queryKey: ["rentals", "fieldTicket", id],
    queryFn: async (): Promise<FieldTicket | null> => {
      const raw = unwrap<Record<string, unknown>>((await api.rentals.tickets.get(id)).data);
      return raw && raw.id ? toTicket(raw) : null;
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const t = query.data ?? null;
  const loading = query.isLoading && !failure;

  const actions = t ? (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" className={BAND_SECONDARY} onClick={handleDispute}>
        Dispute
      </button>
      {t.status !== "invoiced" && (
        <button type="button" className={PRIMARY} onClick={handleApproveInvoice}>
          <CheckCircle2 className="size-4" aria-hidden="true" />
          {t.status === "approved" ? "Generate Invoice" : "Approve → Invoice"}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="rental-field-ticket-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals · Field ticket"
        title={t ? t.ticket_number ?? "—" : loading ? "Loading…" : "Field ticket"}
        onBack={() => navigate("/rentals/tickets")}
        backLabel="Back to Field Tickets"
        actions={actions}
        stats={
          !t
            ? undefined
            : [
                { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: "Total", sub: t.tax_type ? `Incl. ${t.tax_type}` : "After tax", tone: "success", value: fmtCurrency(t.total) },
                { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: "Days", sub: "On the ticket", tone: "accent", value: displayValue(t.total_days) },
                { icon: <ListOrdered className="size-4" aria-hidden="true" />, label: "Lines", sub: "Billed items", tone: "info", value: displayValue(t.line_items?.length) },
                { icon: <Hash className="size-4" aria-hidden="true" />, label: "PO", sub: "Customer PO", tone: "neutral", value: t.po_number ?? "—" },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading ticket" />
            </div>
          ) : !t ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this ticket" />
              ) : (
                <DhEmpty>This ticket does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            <TicketBody t={t} timeZone={timeZone} />
          )}
        </div>
      </section>
    </div>
  );
}

function TicketBody({ t, timeZone }: { t: FieldTicket; timeZone: string | null }) {
  const lines = t.line_items ?? [];
  return (
    <>
      <DhPanel eyebrow="Ticket" title="Ticket record" testId="rental-ticket-record">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
          <DhSpec label="Ticket #" value={t.ticket_number ?? "—"} mono />
          <DhSpec label="Status" value={<StatusBadge status={t.status} />} />
          <DhSpec label="Type" value={t.ticket_type ? TICKET_TYPE[t.ticket_type] ?? "—" : "—"} />
          <DhSpec label="Customer" value={t.customer_name ?? "—"} />
          <DhSpec label="Wellsite" value={t.well_name ?? "—"} />
          <DhSpec label="PO" value={t.po_number ?? "—"} />
          <div data-testid="ticket-period" className="min-w-0">
            <DhSpec label="Rental period" value={periodLabel(t)} />
          </div>
          <DhSpec label="Daily rate" value={fmtCurrency(t.daily_rate)} mono />
          <DhSpec label="Created" value={formatInstant(t.created_at, timeZone) ?? "—"} />
        </dl>
      </DhPanel>

      <DhPanel
        eyebrow="Rental"
        title="Line items"
        testId="rental-ticket-lines"
        footer={lines.length ? `${lines.length} ${lines.length === 1 ? "line" : "lines"}` : undefined}
      >
        {lines.length === 0 ? (
          <DhEmpty>No line items on this ticket.</DhEmpty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                <tr>
                  <th scope="col" className={TH}>Item</th>
                  <th scope="col" className={TH}>Asset</th>
                  <th scope="col" className={`${TH} text-right`}>Days</th>
                  <th scope="col" className={`${TH} text-right`}>Rate</th>
                  <th scope="col" className={`${TH} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.id ?? i} className="border-t border-fieldiq-border">
                    <td className={`${TD} font-medium`}>{l.description ?? "—"}</td>
                    <td className={`${TD} font-mono`}>{l.asset_number ?? l.asset_name ?? "—"}</td>
                    <td className={TD_NUM}>{displayValue(l.days)}</td>
                    <td className={TD_NUM}>{fmtCurrency(l.rate)}</td>
                    <td className={`${TD_NUM} font-extrabold text-white`}>{fmtCurrency(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* The ticket's STORED figures. The TOTAL tax and its name (api#1019) —
            not gst_amount, which is only the GST share and is null under HST or
            a US state. */}
        <dl className="ml-auto w-full max-w-sm space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-fieldiq-muted">Subtotal</dt>
            <dd data-testid="ticket-subtotal" className="font-mono text-fieldiq-text">{fmtCurrency(t.subtotal)}</dd>
          </div>
          <div className="flex justify-between">
            <dt data-testid="ticket-tax-label" className="text-fieldiq-muted">{t.tax_type ?? "Tax"}</dt>
            <dd data-testid="ticket-tax" className="font-mono text-fieldiq-text">{fmtCurrency(t.tax_amount)}</dd>
          </div>
          <div className="flex justify-between border-t border-fieldiq-border pt-1 font-extrabold">
            <dt className="text-white">Total</dt>
            <dd data-testid="ticket-total" className="font-mono text-white">{fmtCurrency(t.total)}</dd>
          </div>
        </dl>
      </DhPanel>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SignOffCard label="Field Supervisor Sign-Off" signed={signedFlag(t.supervisor_signed)} />
        <SignOffCard label="Customer Sign-Off" signed={signedFlag(t.customer_signed)} />
      </div>
    </>
  );
}

/**
 * What a rentals ticket records about a sign-off: whether it happened. No
 * signer, time or image is stored, so none is shown (#803).
 */
function SignOffCard({ label, signed }: { label: string; signed: boolean }) {
  return (
    <section
      role="group"
      aria-label={label}
      className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4"
    >
      <h3 className="mb-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{label}</h3>
      <div className="flex h-20 items-center justify-center rounded-fi-control border-2 border-dashed border-fieldiq-border">
        {signed ? (
          <div className="text-center text-fieldiq-muted">
            <CheckCircle2 className="mx-auto mb-1 h-6 w-6 text-fieldiq-text" aria-hidden="true" />
            <p className="text-xs">Signed</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleSendForSignature}
            className="inline-flex items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-1.5 text-sm font-bold text-fieldiq-text transition hover:bg-fieldiq-dark/50"
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Send for Signature
          </button>
        )}
      </div>
    </section>
  );
}
